import { useCallback, useEffect, useRef, useState } from "react";
import {
  Copy, Globe2, LogOut, ShieldCheck, Ticket, WalletCards, Users,
  CalendarDays, Sparkles, Trophy, ArrowRight, ArrowLeft, Gift, Check,
  Share2, Link2, X, Coins, Award, Gem, Crown, Palette, Wallet, TrendingUp,
  LifeBuoy,
} from "lucide-react";
import { LANGUAGES, RTL_LANGS, t } from "./i18n";
import { SupportCenter } from "./components/SupportCenter";
import { AdminSupportPanel } from "./components/AdminSupportPanel";
import { api } from "./api";
import "@/App.css";

const money = (n) => `$${Number(n).toLocaleString()}`;

const PRIZE_LABEL_KEYS = ["prize_1", "prize_2", "prize_3", "prize_4_7", "prize_8_10"];

function DrawCountdown({ drawDate, tier, lang }) {
  const calculate = useCallback(() => {
    const target = /^\d{4}-\d{2}-\d{2}$/.test(drawDate || "")
      ? Date.parse(`${drawDate}T00:00:00Z`)
      : Number.NaN;
    if (!Number.isFinite(target)) return null;
    const total = Math.max(0, target - Date.now());
    return {
      total,
      days: Math.floor(total / 86400000),
      hours: Math.floor((total / 3600000) % 24),
      minutes: Math.floor((total / 60000) % 60),
      seconds: Math.floor((total / 1000) % 60),
    };
  }, [drawDate]);
  const [remaining, setRemaining] = useState(calculate);

  useEffect(() => {
    setRemaining(calculate());
    const timer = window.setInterval(() => setRemaining(calculate()), 1000);
    return () => window.clearInterval(timer);
  }, [calculate]);

  if (!remaining) return null;
  if (remaining.total === 0) {
    return <div className="draw-ready" data-testid={`pool-${tier}-draw-ready`}>{t(lang, "draw_ready")}</div>;
  }

  const units = [
    [remaining.days, "countdown_days"],
    [remaining.hours, "countdown_hours"],
    [remaining.minutes, "countdown_minutes"],
    [remaining.seconds, "countdown_seconds"],
  ];
  return (
    <div className="draw-countdown" data-testid={`pool-${tier}-countdown`}>
      {units.map(([value, label]) => (
        <div className="countdown-unit" key={label}>
          <strong>{String(value).padStart(2, "0")}</strong>
          <span>{t(lang, label)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- TopBar ----------
function Topbar({ user, lang, setLang, onLogout, onSignIn }) {
  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">✦</span>
        <span>LOTTO<span className="gold">/</span> WORLD</span>
      </div>
      <div className="top-actions">
        <div className="language">
          <Globe2 size={15} />
          <select
            data-testid="language-selector"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>
        {user && (
          <>
            <span className="identity" data-testid="user-identity">{user.name}</span>
            <button
              className="icon-btn"
              data-testid="logout-button"
              onClick={onLogout}
              title={t(lang, "logout_title")}
            >
              <LogOut size={17} />
            </button>
          </>
        )}
      </div>
    </header>
  );
}

// ---------- Auth ----------
function Auth({ onAuth, lang, onClose }) {
  const [mode, setMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("ref") ? "register" : "login";
  });
  const [seedMode, setSeedMode] = useState(false);
  const [form, setForm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || "";
    return { name: "", identifier: "", password: "", referral_code: ref };
  });
  const [seed, setSeed] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const url = seedMode ? "/auth/seed-login" : mode === "login" ? "/auth/login" : "/auth/register";
      const payload = seedMode ? { seed_phrase: seed } : form;
      const { data } = await api.post(url, payload);
      if (data.seed_phrase) {
        setPendingUser(data.user);
        setSeed(data.seed_phrase);
      } else onAuth(data.user || data);
    } catch (err) {
      setError(err.response?.data?.detail || t(lang, "check_login"));
    }
  };

  if (seed && mode === "register") {
    return (
      <div className="modal-backdrop">
        <div className="modal auth-modal seed-panel" data-testid="seed-modal">
          <div className="eyebrow">{t(lang, "recovery_vault")}</div>
          <h2>{t(lang, "seed_h1")}</h2>
          <p className="muted">{t(lang, "seed_desc")}</p>
          <div className="seed-box" data-testid="seed-phrase-display">{seed}</div>
          <div className="seed-warn">
            <ShieldCheck size={15} />
            <span><strong>{t(lang, "important")}</strong> {t(lang, "seed_warn")}</span>
          </div>
          <button className="ghost wide" data-testid="copy-seed-button" onClick={() => navigator.clipboard.writeText(seed)}>
            <Copy size={16} /> {t(lang, "copy_phrase")}
          </button>
          <button
            className="primary wide seed-continue-cta"
            data-testid="seed-continue-button"
            onClick={() => { setSeed(""); onAuth(pendingUser); }}
          >
            {t(lang, "saved_continue")} <ArrowRight size={17} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal auth-modal" data-testid="auth-modal">
        {onClose && <button className="modal-close" data-testid="auth-close-button" onClick={onClose}>×</button>}
        <div className="eyebrow">
          {seedMode ? t(lang, "recovery_login") : mode === "login" ? t(lang, "welcome_back") : t(lang, "join_network")}
        </div>
        <h2>{seedMode ? t(lang, "use_seed_phrase") : mode === "login" ? t(lang, "enter_draw") : t(lang, "create_account")}</h2>
        <p className="muted">{t(lang, "seed_tagline")}</p>
        <form onSubmit={submit} data-testid="auth-form">
          {!seedMode && mode === "register" && (
            <label>{t(lang, "name_label")}
              <input data-testid="register-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder={t(lang, "name_ph")} />
            </label>
          )}
          {seedMode ? (
            <label>{t(lang, "seed_field_label")}
              <textarea data-testid="seed-login-input" value={seed} onChange={(e) => setSeed(e.target.value)} required placeholder={t(lang, "seed_field_ph")} />
            </label>
          ) : (
            <>
              <label>{t(lang, "email_or_mobile")}
                <input data-testid="auth-identifier-input" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} required placeholder={t(lang, "identifier_ph")} />
              </label>
              <label>{t(lang, "password")}
                <input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder="••••••••" />
              </label>
              {mode === "register" && (
                <label>{t(lang, "referral_code")} <span className="optional">{t(lang, "optional")}</span>
                  <input data-testid="referral-code-input" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} placeholder={t(lang, "referral_ph")} />
                </label>
              )}
            </>
          )}
          {error && <div className="error" data-testid="auth-error">{error}</div>}
          <button className="primary wide" data-testid="auth-submit-button">
            {seedMode ? t(lang, "recover_account") : mode === "login" ? t(lang, "continue_btn") : t(lang, "create_btn")}
          </button>
        </form>
        <div className="auth-links">
          {!seedMode && mode === "login" && (
            <button className="text-btn" data-testid="seed-login-toggle" onClick={() => setSeedMode(true)}>{t(lang, "use_seed_toggle")}</button>
          )}
          {seedMode && (
            <button className="text-btn" data-testid="password-login-toggle" onClick={() => setSeedMode(false)}>{t(lang, "back_to_password")}</button>
          )}
          {!seedMode && (
            <button className="text-btn" data-testid="auth-mode-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? t(lang, "new_here") : t(lang, "already_registered")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Pool Card ----------
function PoolCard({ pool, onBuy, onViewPrizes, lang }) {
  const progress = Math.min(100, (pool.sold / pool.total) * 100);
  const prizes = pool.prizes || [];
  const topPrize = prizes[0]?.amount || 0;
  const secondPrize = prizes[1]?.amount || 0;
  const thirdPrize = prizes[2]?.amount || 0;
  const TierIcon = { 1: Coins, 2: Award, 3: Gem, 4: Crown }[pool.tier] || Coins;
  const tierTag = t(lang, `pool_tag_${pool.tier}`);
  return (
    <section
      className={`pool-row pool-tier-${pool.tier} ${pool.tier === 4 ? "pool-mega" : ""}`}
      data-tier={pool.tier}
      data-testid={`pool-${pool.tier}-card`}
    >
      <div className="pool-side">
        <div className="pool-number">0{pool.tier}</div>
        <div className="tier-chip">{pool.name.split(" ")[0].toUpperCase()}</div>
      </div>
      <div className="pool-main">
        <div className="pool-head">
          <div className="pool-price-block">
            <span className="eyebrow">{pool.name}{pool.tier === 4 && <span className="mega-badge">MEGA</span>}</span>
            <h2>{money(pool.price)} <small>{t(lang, "per_ticket")}</small></h2>
          </div>
          <div className="pool-emblem" data-testid={`pool-${pool.tier}-emblem`}>
            <div className="emblem-halo" aria-hidden="true"><span/><span/><span/></div>
            <div className="emblem-ring">
              <TierIcon size={38} strokeWidth={1.4} />
            </div>
            <div className="emblem-tag" data-testid={`pool-${pool.tier}-tag`}>
              <Sparkles size={11} /> {tierTag}
            </div>
          </div>
          <div className="draw-date" data-testid={`pool-${pool.tier}-draw-panel`}>
            <CalendarDays size={15} />
            <div className="draw-date-details">
              <span>{t(lang, "draw_date")} · 00:00 UTC</span>
              <strong data-testid={`pool-${pool.tier}-draw-date`}>{pool.draw_date}</strong>
              <DrawCountdown drawDate={pool.draw_date} tier={pool.tier} lang={lang} />
            </div>
          </div>
        </div>
        <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
        <div className="pool-foot">
          <span data-testid={`pool-${pool.tier}-sold`}>
            {pool.sold.toLocaleString()} {t(lang, "sold")} <i>/</i> {pool.total.toLocaleString()} {t(lang, "total")}
          </span>
          <span className="remaining">{pool.remaining.toLocaleString()} {t(lang, "remaining")}</span>
          <button className="primary" data-testid={`pool-${pool.tier}-buy-button`} onClick={() => onBuy(pool)}>
            {t(lang, "buy_tickets")} <Ticket size={16} />
          </button>
        </div>
        <div className={`prize-teaser ${pool.tier === 4 ? "prize-teaser-mega" : ""}`} data-testid={`pool-${pool.tier}-prize-teaser`}>
          <div className="prize-teaser-top">
            <div className="prize-badge"><Trophy size={16} /></div>
            <div className="prize-teaser-text">
              <span className="eyebrow gold-tone">{t(lang, "biggest_prize")}</span>
              <strong data-testid={`pool-${pool.tier}-top-prize`}>{money(topPrize)}</strong>
            </div>
            <div className="mini-pills" aria-hidden="true">
              <span className="mini-pill">{money(secondPrize)}</span>
              <span className="mini-pill">{money(thirdPrize)}</span>
              <span className="mini-pill">+7</span>
            </div>
          </div>
          <button
            className="prize-link"
            data-testid={`pool-${pool.tier}-prize-link`}
            onClick={() => onViewPrizes(pool)}
          >
            {t(lang, "view_prizes")} <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------- Prize Breakdown Modal ----------
function PrizeBreakdown({ pool, onClose, lang }) {
  const prizes = pool.prizes || [];
  const total = pool.prize_total || prizes.reduce((s, p) => s + p.amount * p.winners, 0);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal prize-modal ${pool.tier === 4 ? "prize-modal-mega" : ""}`} data-testid="prize-breakdown-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" data-testid="prize-breakdown-close" onClick={onClose}>×</button>
        <div className="eyebrow">{t(lang, "prize_structure")}{pool.tier === 4 && <span className="mega-badge">MEGA</span>}</div>
        <h2>{pool.name} · {money(pool.price)} {t(lang, "pool_word")}</h2>
        <p className="muted">{t(lang, "live_prize_line")}</p>
        <div className="prize-table" data-testid="prize-table">
          {prizes.map((p, i) => (
            <div className="prize-row" key={i}>
              <span className="prize-rank">{t(lang, PRIZE_LABEL_KEYS[i] || "prize_1")}</span>
              <span className="prize-winners">
                {p.winners} {p.winners === 1 ? t(lang, "winner") : t(lang, "winners")}
              </span>
              <strong className="prize-amount">
                {money(p.amount)}{p.winners > 1 ? ` ${t(lang, "each_suffix")}` : ""}
              </strong>
            </div>
          ))}
        </div>
        <div className="prize-total">
          <span>{t(lang, "total_prize_pool")}</span>
          <strong>{money(total)}</strong>
        </div>
      </div>
    </div>
  );
}

// ---------- Checkout (2-step) ----------
function Checkout({ pool, config, onClose, onComplete, lang }) {
  const [step, setStep] = useState(1); // 1 = quantity, 2 = payment
  const [quantity, setQuantity] = useState(1);
  const [tx, setTx] = useState("");
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const txInputRef = useRef(null);
  const total = quantity * pool.price;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(config.usdt_address)}`;

  const pay = async () => {
    setError("");
    if (!tx.trim()) {
      setError(t(lang, "tx_hash_required"));
      window.requestAnimationFrame(() => {
        txInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        txInputRef.current?.focus({ preventScroll: true });
      });
      return;
    }
    try {
      const { data } = await api.post("/payments/order", { tier: pool.tier, quantity, tx_hash: tx.trim() });
      setDone(data);
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || t(lang, "could_not_order"));
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal checkout-modal-shell" data-testid="checkout-modal">
        <button className="modal-close" data-testid="checkout-close-button" onClick={onClose}>×</button>
        {done ? (
          <div className="modal-body">
            <div className="success-icon"><Check size={32} /></div>
            <h2>{t(lang, "tickets_reserved")}</h2>
            <p className="muted">{t(lang, "pending_confirmation")}</p>
            <div className="order-result" data-testid="order-result">
              {done.tickets.map((tk) => <span key={tk.code}>{tk.code}</span>)}
            </div>
            <button className="primary wide" data-testid="checkout-done-button" onClick={onClose}>
              {t(lang, "done")}
            </button>
          </div>
        ) : step === 1 ? (
          <>
            <div className="modal-body">
              <div className="eyebrow">{pool.name} · {money(pool.price)}</div>
              <h2>{t(lang, "reserve_tickets", { name: pool.name })}</h2>
              <div className="qty-block">
                <label>{t(lang, "quantity")}
                  <input
                    data-testid="ticket-quantity-input"
                    type="number"
                    min="1"
                    max="100"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                  />
                </label>
                <div className="qty-steppers">
                  {[1, 2, 3, 5, 10, 25].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={quantity === n ? "qty-chip active" : "qty-chip"}
                      data-testid={`qty-preset-${n}`}
                      onClick={() => setQuantity(n)}
                    >{n}</button>
                  ))}
                </div>
              </div>
              <div className="total-line">
                <span>{t(lang, "checkout_total")}</span>
                <strong data-testid="checkout-total">{money(total)} USDT</strong>
              </div>
            </div>
            <div className="checkout-footer">
              <button
                className="primary wide buy-cta"
                data-testid="proceed-to-payment-button"
                onClick={() => setStep(2)}
              >
                {t(lang, "proceed_to_payment")} <ArrowRight size={17} />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <button className="text-btn back-link" data-testid="checkout-back-button" onClick={() => setStep(1)}>
                <ArrowLeft size={14} /> {t(lang, "back_btn")}
              </button>
              <div className="eyebrow">USDT · {config.usdt_network}</div>
              <h2>{t(lang, "review_and_pay")}</h2>
              <p className="muted">{t(lang, "send_usdt_note")}</p>
              <div className="checkout-grid">
                <div>
                  <div className="summary-block">
                    <div className="section-label"><span>{t(lang, "order_summary")}</span></div>
                    <div className="summary-row">
                      <span>{quantity} × {pool.name} {t(lang, "tickets_word")}</span>
                      <strong>{money(total)}</strong>
                    </div>
                  </div>
                  <label>{t(lang, "tx_hash_label")} <span className="optional">{t(lang, "after_sending")}</span>
                    <input
                      ref={txInputRef}
                      className={error === t(lang, "tx_hash_required") ? "input-attention" : ""}
                      data-testid="transaction-hash-input"
                      value={tx}
                      onChange={(e) => {
                        setTx(e.target.value);
                        if (error) setError("");
                      }}
                      aria-invalid={error === t(lang, "tx_hash_required")}
                      aria-describedby={error ? "checkout-error" : undefined}
                      placeholder="0x..."
                    />
                  </label>
                </div>
                <div className="qr-side">
                  <img data-testid="usdt-qr-code" src={qr} alt="USDT QR code" />
                  <code data-testid="usdt-wallet-address">{config.usdt_address}</code>
                  <button
                    className="text-btn"
                    data-testid="copy-wallet-button"
                    onClick={() => navigator.clipboard.writeText(config.usdt_address)}
                  >
                    <Copy size={14} /> {t(lang, "copy_address")}
                  </button>
                </div>
              </div>
              {error && <div id="checkout-error" className="error" data-testid="checkout-error" role="alert">{error}</div>}
              <div className="submit-note">
                <ShieldCheck size={14} /> <span>{t(lang, "payment_step_note")}</span>
              </div>
            </div>
            <div className="checkout-footer">
              <button className="primary wide submit-cta" data-testid="submit-payment-button" onClick={pay}>
                <Check size={17} /> {t(lang, "confirm_submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Ticket Card Modal ----------
function TicketCard({ ticket, pool, lang, onClose }) {
  const barcode = ticket.code.split("").map((c, i) => (
    <span key={i} style={{ width: (c.charCodeAt(0) % 3) + 2 + "px" }} />
  ));
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="ticket-card-modal" data-testid="ticket-card-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" data-testid="ticket-card-close" onClick={onClose}><X size={20}/></button>
        <div className="ticket-card">
          <div className="ticket-card-top">
            <div className="ticket-brand">
              <span className="brand-mark">✦</span>
              <span>LOTTO<span className="gold">/</span> WORLD</span>
            </div>
            <span className={`ticket-status status-${ticket.status}`}>{ticket.status.replace(/_/g, " ")}</span>
          </div>
          <div className="ticket-perf" aria-hidden="true"><span className="notch left"/><div className="dashed"/><span className="notch right"/></div>
          <div className="ticket-card-body">
            <div className="ticket-tier-label">{pool?.name || `${t(lang, "tier_prefix")} ${ticket.tier}`}</div>
            <div className="ticket-price">{pool ? `$${pool.price}` : ""} <small>{t(lang, "per_ticket")}</small></div>
            <div className="ticket-code-block">
              <span className="ticket-code-label">TICKET NO.</span>
              <div className="ticket-code-value" data-testid="ticket-card-code">{ticket.code}</div>
              <div className="ticket-barcode" aria-hidden="true">{barcode}</div>
            </div>
            <div className="ticket-meta-grid">
              <div>
                <span>{t(lang, "draw_date")}</span>
                <strong>{pool?.draw_date || "—"}</strong>
              </div>
              <div>
                <span>PURCHASED</span>
                <strong>{new Date(ticket.created_at).toLocaleDateString()}</strong>
              </div>
              <div>
                <span>{t(lang, "biggest_prize")}</span>
                <strong className="prize-hint">${(pool?.prizes?.[0]?.amount || 200000).toLocaleString()}</strong>
              </div>
            </div>
          </div>
          <div className="ticket-footer">
            <ShieldCheck size={12}/> <span>{t(lang, "provably_fair_note")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Referral Progress ----------
function ReferralProgress({ count, lang }) {
  const target = 5;
  const inCycle = count % target;
  const earned = Math.floor(count / target);
  const remaining = target - inCycle;
  const dots = Array.from({ length: target });
  const justUnlocked = count > 0 && inCycle === 0;

  return (
    <section className="referral-progress" data-testid="referral-progress">
      <div className="ref-progress-head">
        <div>
          <div className="eyebrow gold-tone">{t(lang, "referral_progress_title")}</div>
          <p className="muted">{t(lang, "referral_progress_sub")}</p>
        </div>
        <div className="ref-progress-stats">
          <div className="ref-stat">
            <strong data-testid="referral-count">{count}</strong>
            <span>{t(lang, "friends_joined")}</span>
          </div>
          <div className="ref-stat gift">
            <strong data-testid="referral-free-tickets"><Gift size={18} /> {earned}</strong>
            <span>{t(lang, "free_tickets_earned")}</span>
          </div>
        </div>
      </div>
      <div className="ref-dots" data-testid="referral-dots">
        {dots.map((_, i) => (
          <div key={i} className={i < inCycle ? "ref-dot filled" : "ref-dot"}>
            {i < inCycle ? <Check size={12} /> : i + 1}
          </div>
        ))}
      </div>
      <div className="ref-progress-note">
        {justUnlocked
          ? <span className="unlock-note"><Sparkles size={14} /> {t(lang, "all_earned")}</span>
          : <span>{remaining} {t(lang, "to_next_free_ticket")}</span>}
      </div>
    </section>
  );
}

// ---------- Home Referral ----------
function HomeReferral({ user, lang, onSignIn }) {
  const [copied, setCopied] = useState(false);
  const referralUrl = user ? `${window.location.origin}/?ref=${user.referral_code}` : "";

  const copyReferral = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const shareReferral = async () => {
    const shareData = {
      title: "LOTTO WORLD",
      text: t(lang, "referral_progress_sub"),
      url: referralUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    await copyReferral();
  };

  return (
    <section className="home-referral-section" data-testid="home-referral-section">
      <div className="home-referral-copy">
        <div className="eyebrow gold-tone" data-testid="home-referral-eyebrow">
          <Gift size={14} /> {t(lang, "referral_progress_title")}
        </div>
        <h2 data-testid="home-referral-headline">{t(lang, "referral_progress_sub")}</h2>
        <p className="muted" data-testid="home-referral-description">
          {t(lang, "referral_reward_detail")}
        </p>
      </div>
      <div className="home-referral-reward" data-testid="home-referral-reward-badge">
        <strong>5</strong>
        <span>{t(lang, "friends_joined")}</span>
        <ArrowRight size={18} />
        <strong>1</strong>
        <span>{t(lang, "free_ticket")}</span>
      </div>
      {user ? (
        <div className="home-referral-actions" data-testid="home-referral-actions">
          <div className="ref-share-url" data-testid="home-referral-url">
            <Link2 size={15} />
            <code>{referralUrl}</code>
          </div>
          <div className="ref-share-buttons">
            <button className="primary" data-testid="home-referral-share-button" onClick={shareReferral}>
              <Share2 size={16} /> {t(lang, "share_invite")}
            </button>
            <button className="ghost" data-testid="home-referral-copy-button" onClick={copyReferral}>
              {copied ? <><Check size={16} /> {t(lang, "copied")}</> : <><Copy size={16} /> {t(lang, "copy_link")}</>}
            </button>
          </div>
        </div>
      ) : (
        <button className="primary home-referral-signin" data-testid="home-referral-signin-cta" onClick={onSignIn}>
          {t(lang, "signin_referral")} <ArrowRight size={16} />
        </button>
      )}
    </section>
  );
}

// ---------- My Winnings ----------
function MyWinnings({ lang }) {
  const [data, setData] = useState({ balance: 0, total_won: 0, winnings: [], withdrawals: [] });
  const [form, setForm] = useState({ amount: "", address: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const load = async () => {
    const r = await api.get("/winnings");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const withdraw = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      await api.post("/winnings/withdraw", { amount: parseFloat(form.amount), address: form.address });
      setMsg("Withdrawal requested. Admin will process it shortly.");
      setForm({ amount: "", address: "" });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Withdrawal failed");
    }
  };

  return (
    <section className="winnings-panel" data-testid="my-winnings-panel">
      <div className="winnings-head">
        <div className="eyebrow gold-tone">MY WINNINGS</div>
        <p className="muted">Prizes credited from drawn tickets · withdraw to your BEP20 wallet</p>
      </div>
      <div className="winnings-stats">
        <div className="winning-stat balance-stat">
          <span>Available balance</span>
          <strong data-testid="winnings-balance">{money(data.balance || 0)}</strong>
          <em>USDT</em>
        </div>
        <div className="winning-stat">
          <TrendingUp size={17} />
          <span>Total won</span>
          <strong data-testid="winnings-total-won">{money(data.total_won || 0)}</strong>
        </div>
        <div className="winning-stat">
          <Wallet size={17} />
          <span>Withdrawals</span>
          <strong data-testid="winnings-withdrawal-count">{data.withdrawals.length}</strong>
        </div>
      </div>
      <form className="withdraw-form" onSubmit={withdraw} data-testid="withdraw-form">
        <div className="section-label"><span>REQUEST WITHDRAWAL</span></div>
        <div className="withdraw-grid">
          <label>Amount (USDT)
            <input
              data-testid="withdraw-amount-input"
              type="number"
              min="1"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              required
            />
          </label>
          <label>BEP20 wallet address
            <input
              data-testid="withdraw-address-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="0x..."
              required
            />
          </label>
        </div>
        {err && <div className="error" data-testid="withdraw-error">{err}</div>}
        {msg && <div className="success-note" data-testid="withdraw-success">{msg}</div>}
        <button
          className="primary wide"
          data-testid="withdraw-submit-button"
          disabled={(data.balance || 0) <= 0}
        >
          <Wallet size={16} /> Withdrawal
        </button>
      </form>
      <div className="section-label"><span>WINNING HISTORY</span><span>{data.winnings.length} total</span></div>
      <div className="winning-list" data-testid="winnings-list">
        {data.winnings.length === 0 ? (
          <div className="empty" data-testid="no-winnings">No winnings yet. Prizes will appear here after each draw.</div>
        ) : data.winnings.map((w, i) => (
          <div className="winning-row" key={w.id || i}>
            <div className="win-rank">#{w.rank}</div>
            <div>
              <strong>{w.ticket_code}</strong>
              <span>Tier {w.tier} · {new Date(w.created_at).toLocaleDateString()}</span>
            </div>
            <div className="win-amount">{money(w.amount)}</div>
          </div>
        ))}
      </div>
      {data.withdrawals.length > 0 && (
        <>
          <div className="section-label"><span>WITHDRAWAL REQUESTS</span></div>
          <div className="withdraw-list" data-testid="my-withdrawals-list">
            {data.withdrawals.map((w) => (
              <div className="withdraw-row" key={w.id}>
                <div>
                  <strong>{money(w.amount)}</strong>
                  <code>{w.address}</code>
                </div>
                <div className={`pay-status status-${w.status}`}>{w.status}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ---------- Profile ----------
function Profile({ user, setView, lang, pools }) {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/tickets").then((r) => setTickets(r.data)); }, []);

  const referralUrl = `${window.location.origin}/?ref=${user.referral_code}`;
  const shareData = {
    title: "LOTTO WORLD",
    text: t(lang, "referral_progress_sub"),
    url: referralUrl,
  };
  const doShare = async () => {
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (_) { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  const doCopy = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const poolFor = (tier) => (pools || []).find((p) => p.tier === tier);

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <div className="eyebrow">{t(lang, "account_ledger")}</div>
          <h1>{t(lang, "my_tickets_h1")}</h1>
        </div>
        <div className="heading-actions">
          <button className="ghost" data-testid="profile-support-button" onClick={() => setView("support")}>
            <LifeBuoy size={16} /> Support
          </button>
          <button className="ghost" data-testid="profile-back-button" onClick={() => setView("home")}>
            {t(lang, "back_to_pools")}
          </button>
        </div>
      </div>
      <section className="profile-strip">
        <div className="avatar">{user.name?.slice(0, 1)}</div>
        <div>
          <strong data-testid="profile-name">{user.name}</strong>
          <span data-testid="profile-identifier">{user.identifier}</span>
        </div>
        <div className="ref-box">
          <span>{t(lang, "your_referral_code")}</span>
          <code data-testid="profile-referral-code">{user.referral_code}</code>
        </div>
      </section>
      <ReferralProgress count={user.referral_count || 0} lang={lang} />
      <MyWinnings lang={lang} />
      <section className="referral-share" data-testid="referral-share">
        <div className="ref-share-head">
          <div>
            <div className="eyebrow gold-tone">SHARE & EARN</div>
            <p className="muted">{t(lang, "referral_progress_sub")}</p>
          </div>
        </div>
        <div className="ref-share-url" data-testid="referral-url">
          <Link2 size={15}/>
          <code>{referralUrl}</code>
        </div>
        <div className="ref-share-buttons">
          <button className="primary" data-testid="referral-share-button" onClick={doShare}>
            <Share2 size={16}/> Share invite
          </button>
          <button className="ghost" data-testid="referral-copy-button" onClick={doCopy}>
            {copied ? <><Check size={16}/> Copied</> : <><Copy size={16}/> Copy link</>}
          </button>
        </div>
      </section>
      <div className="section-label">
        <span>{t(lang, "booked_tickets")}</span>
        <span>{tickets.length} {t(lang, "total_count")}</span>
      </div>
      <div className="ticket-list">
        {tickets.length ? tickets.map((tk) => (
          <button
            className="ticket-item ticket-item-btn"
            key={tk.id}
            data-testid="ticket-row"
            onClick={() => setSelectedTicket(tk)}
          >
            <div className="ticket-icon"><Ticket size={18} /></div>
            <strong>{tk.code}</strong>
            <span>{t(lang, "tier_prefix")} {tk.tier} · {new Date(tk.created_at).toLocaleDateString()}</span>
            <b className={`ticket-status-mini status-${tk.status}`}>{tk.status.replace(/_/g, " ")}</b>
            <span className="view-ticket-cta" data-testid="view-ticket-cta">
              <Ticket size={13} /> View ticket <ArrowRight size={12} />
            </span>
          </button>
        )) : (
          <div className="empty" data-testid="empty-tickets">{t(lang, "no_tickets")}</div>
        )}
      </div>
      {selectedTicket && (
        <TicketCard
          ticket={selectedTicket}
          pool={poolFor(selectedTicket.tier)}
          lang={lang}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </div>
  );
}

// ---------- Draw Tool ----------
function DrawTool({ tier, lang }) {
  const [result, setResult] = useState(null);
  const draw = async () => {
    try {
      const { data } = await api.post(`/admin/draw/${tier}`);
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || t(lang, "draw_unavailable") });
    }
  };
  return (
    <section className="tool-panel draw-tool">
      <div className="section-label"><span>{t(lang, "provably_fair")}</span></div>
      <h2>{t(lang, "run_draw", { tier })}</h2>
      <p className="muted">{t(lang, "draw_transparency")}</p>
      <button className="primary" data-testid="admin-run-draw-button" onClick={draw}>
        {t(lang, "run_secure")} <Sparkles size={16} />
      </button>
      {result?.error && <div className="error" data-testid="draw-error">{result.error}</div>}
      {result?.winners && (
        <div className="winner-list" data-testid="draw-results">
          <code>Seed: {result.seed}</code>
          {result.winners.map((w) => (
            <div key={w.code}><b>#{w.rank}</b><span>{w.code}</span></div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Payment Approvals ----------
function PaymentApprovals({ lang, notify }) {
  const [filter, setFilter] = useState("pending_confirmation");
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/payments?status=${filter}`);
    setPayments(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/payments/${id}/${action}`);
      notify(action === "approve" ? t(lang, "payment_approved") : t(lang, "payment_rejected"));
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || "Failed");
    } finally {
      setBusy("");
    }
  };

  const filters = [
    ["pending_confirmation", t(lang, "filter_pending")],
    ["approved", t(lang, "filter_approved")],
    ["rejected", t(lang, "filter_rejected")],
    ["all", t(lang, "filter_all")],
  ];

  const statusLabel = (s) => s === "approved" ? t(lang, "payment_approved") : s === "rejected" ? t(lang, "payment_rejected") : t(lang, "payment_pending");

  return (
    <section className="tool-panel payments-panel" data-testid="admin-payments-panel">
      <div className="section-label"><span>{t(lang, "payment_approvals")}</span></div>
      <h2>{t(lang, "review_payments")}</h2>
      <p className="muted">{t(lang, "payments_desc")}</p>
      <div className="tabs">
        {filters.map(([key, label]) => (
          <button
            key={key}
            className={filter === key ? "tab active" : "tab"}
            data-testid={`payments-filter-${key}`}
            onClick={() => setFilter(key)}
          >{label}</button>
        ))}
      </div>
      <div className="payments-list" data-testid="payments-list">
        {payments.length === 0 ? (
          <div className="empty" data-testid="payments-empty">{t(lang, "no_payments")}</div>
        ) : payments.map((p) => (
          <div className="payment-row" key={p.id} data-testid={`payment-row-${p.id}`}>
            <div className="pay-user">
              <strong>{p.user_name || p.user_identifier || p.user_id.slice(0, 8)}</strong>
              <span>{p.user_identifier}</span>
            </div>
            <div className="pay-meta">
              <span className="pay-tag">{t(lang, "tier_prefix")} {p.tier}</span>
              <span>{p.quantity} × ticket</span>
            </div>
            <div className="pay-amount">
              <strong>{money(p.amount)}</strong>
              <span>USDT</span>
            </div>
            <div className="pay-tx">
              <span className="mini-label">{t(lang, "tx_hash_short")}</span>
              <code data-testid={`payment-tx-${p.id}`}>{p.tx_hash ? p.tx_hash : "—"}</code>
            </div>
            <div className={`pay-status status-${p.status}`}>{statusLabel(p.status)}</div>
            <div className="pay-actions">
              {p.status === "pending_confirmation" ? (
                <>
                  <button
                    className="primary small"
                    data-testid={`approve-${p.id}`}
                    disabled={busy === p.id}
                    onClick={() => act(p.id, "approve")}
                  ><Check size={14} /> {t(lang, "approve")}</button>
                  <button
                    className="ghost small danger"
                    data-testid={`reject-${p.id}`}
                    disabled={busy === p.id}
                    onClick={() => act(p.id, "reject")}
                  >{t(lang, "reject")}</button>
                </>
              ) : (
                <span className="pay-final-note">{new Date(p.reviewed_at || p.created_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Admin Withdrawals ----------
function AdminWithdrawals({ lang, notify }) {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    const r = await api.get(`/admin/withdrawals?status=${filter}`);
    setItems(r.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/withdrawals/${id}/${action}`);
      notify(action === "approve" ? "Withdrawal approved" : "Withdrawal rejected");
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };
  const filters = [
    ["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"],
  ];
  return (
    <section className="tool-panel" data-testid="admin-withdrawals-panel">
      <div className="section-label"><span>WITHDRAWAL REQUESTS</span></div>
      <h2>Process user payouts</h2>
      <p className="muted">Approve after sending USDT to the user&apos;s BEP20 address.</p>
      <div className="tabs">
        {filters.map(([k, l]) => (
          <button
            key={k}
            className={filter === k ? "tab active" : "tab"}
            data-testid={`withdrawals-filter-${k}`}
            onClick={() => setFilter(k)}
          >{l}</button>
        ))}
      </div>
      <div className="payments-list" data-testid="admin-withdrawals-list">
        {items.length === 0 ? (
          <div className="empty" data-testid="withdrawals-empty">No withdrawal requests.</div>
        ) : items.map((w) => (
          <div className="payment-row withdrawal-row" key={w.id} data-testid={`withdrawal-row-${w.id}`}>
            <div className="pay-user">
              <strong>{w.user_name || w.user_identifier || w.user_id.slice(0, 8)}</strong>
              <span>{w.user_identifier}</span>
            </div>
            <div className="pay-amount">
              <strong>{money(w.amount)}</strong>
              <span>USDT</span>
            </div>
            <div className="pay-tx">
              <span className="mini-label">BEP20 ADDRESS</span>
              <code data-testid={`withdrawal-address-${w.id}`}>{w.address}</code>
            </div>
            <div className={`pay-status status-${w.status}`}>{w.status}</div>
            <div className="pay-actions">
              {w.status === "pending" ? (
                <>
                  <button
                    className="primary small"
                    data-testid={`approve-withdrawal-${w.id}`}
                    disabled={busy === w.id}
                    onClick={() => act(w.id, "approve")}
                  ><Check size={14}/> Approve</button>
                  <button
                    className="ghost small danger"
                    data-testid={`reject-withdrawal-${w.id}`}
                    disabled={busy === w.id}
                    onClick={() => act(w.id, "reject")}
                  >Reject</button>
                </>
              ) : (
                <span className="pay-final-note">{new Date(w.reviewed_at || w.created_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Registered Users ----------
function RegisteredUsersModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/users")
      .then((response) => setUsers(response.data))
      .catch((requestError) => setError(requestError.response?.data?.detail || "Could not load registered users"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal registered-users-modal" data-testid="registered-users-modal" onClick={(event) => event.stopPropagation()}>
        <button className="modal-close" data-testid="registered-users-close-button" onClick={onClose}>×</button>
        <div className="eyebrow">REGISTERED USERS</div>
        <div className="registered-users-head">
          <h2>All registered users</h2>
          <strong data-testid="registered-users-count">{users.length.toLocaleString()}</strong>
        </div>
        {loading ? (
          <div className="empty" data-testid="registered-users-loading">Loading users...</div>
        ) : error ? (
          <div className="error" data-testid="registered-users-error" role="alert">{error}</div>
        ) : (
          <div className="registered-users-list" data-testid="registered-users-list">
            {users.length === 0 ? <div className="empty">No registered users.</div> : users.map((registeredUser) => (
              <div className="registered-user-row" key={registeredUser.id} data-testid={`registered-user-row-${registeredUser.id}`}>
                <div className="registered-user-avatar">{registeredUser.name?.slice(0, 1)?.toUpperCase() || "U"}</div>
                <div>
                  <strong data-testid={`registered-user-name-${registeredUser.id}`}>{registeredUser.name || "Unnamed user"}</strong>
                  <span data-testid={`registered-user-identifier-${registeredUser.id}`}>{registeredUser.identifier}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Admin ----------
function Admin({ config, refresh, lang }) {
  const [metrics, setMetrics] = useState({});
  const [tier, setTier] = useState(1);
  const [date, setDate] = useState("");
  const [offline, setOffline] = useState({ customer_name: "", identifier: "", quantity: 1, tier: 1 });
  const [issuing, setIssuing] = useState(false);
  const [notice, setNotice] = useState("");
  const [showUsers, setShowUsers] = useState(false);
  useEffect(() => { api.get("/admin/metrics").then((r) => setMetrics(r.data)); }, []);
  const saveDate = async () => {
    await api.post(`/admin/pools/${tier}/date`, { draw_date: date });
    setNotice(t(lang, "save_date"));
    refresh();
  };
  const issue = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/offline-tickets", offline);
      const selectedPool = config.pools.find((pool) => pool.tier === offline.tier);
      setNotice(`${data.tickets.length} × ${selectedPool?.name || `Tier ${offline.tier}`} ${t(lang, "tickets_word")} allotted`);
      setOffline({ customer_name: "", identifier: "", quantity: 1, tier: offline.tier });
      refresh();
    } catch (error) {
      setNotice(error.response?.data?.detail || "Could not allot offline tickets");
    } finally {
      setIssuing(false);
    }
  };
  const toggleTheme = async () => {
    const next = config.theme === "royal-emerald" ? "midnight-gold" : "royal-emerald";
    await api.post("/admin/theme", { theme: next });
      setNotice(`Theme changed to ${next === "royal-emerald" ? "Jade Coral" : "Midnight Gold"}`);
    refresh();
  };
  const metricDefs = [
    ["users", t(lang, "metric_users"), Users],
    ["tickets", t(lang, "metric_tickets"), Ticket],
    ["revenue", t(lang, "metric_revenue"), WalletCards],
    ["referrals", t(lang, "metric_referrals"), Sparkles],
  ];
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <div className="eyebrow">{t(lang, "control_room_eyebrow")}</div>
          <h1>{t(lang, "admin_console")}</h1>
        </div>
        <div className="admin-header-actions">
          <button className="ghost theme-toggle" data-testid="theme-toggle-button" onClick={toggleTheme}>
            <Palette size={16} /> {config.theme === "royal-emerald" ? "Midnight Gold" : "Jade Coral"}
          </button>
          <div className="admin-badge"><ShieldCheck size={15} /> {t(lang, "admin_verified")}</div>
        </div>
      </div>
      <div className="metric-grid">
        {metricDefs.map(([key, label, I]) => key === "users" ? (
          <button className="metric metric-button" key={key} data-testid="admin-users-metric-button" onClick={() => setShowUsers(true)}>
            <I size={17} />
            <span>{label}</span>
            <strong data-testid={`admin-${key}-metric`}>
              {(metrics[key] || 0).toLocaleString()}
            </strong>
            <ArrowRight className="metric-open-icon" size={15} />
          </button>
        ) : (
          <div className="metric" key={key}>
            <I size={17} />
            <span>{label}</span>
            <strong data-testid={`admin-${key}-metric`}>
              {key === "revenue" ? money(metrics[key] || 0) : (metrics[key] || 0).toLocaleString()}
            </strong>
          </div>
        ))}
      </div>
      <div className="admin-grid">
        <section className="tool-panel">
          <div className="section-label"><span>{t(lang, "draw_calendar")}</span></div>
          <h2>{t(lang, "set_next_draw")}</h2>
          <p className="muted">{t(lang, "draw_desc")}</p>
          <div className="tabs">
            {config.pools.map((p) => (
              <button
                className={tier === p.tier ? "tab active" : "tab"}
                data-testid={`admin-tier-${p.tier}-tab`}
                key={p.tier}
                onClick={() => setTier(p.tier)}
              >
                {t(lang, "tier_prefix")} {p.tier}
              </button>
            ))}
          </div>
          <label>{t(lang, "draw_date_input")}
            <input data-testid="admin-draw-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <button className="primary" data-testid="admin-save-date-button" onClick={saveDate}>
            {t(lang, "save_date")} <CalendarDays size={16} />
          </button>
        </section>
        <section className="tool-panel">
          <div className="section-label"><span>{t(lang, "offline_sales")}</span></div>
          <h2>{t(lang, "issue_direct")}</h2>
          <p className="muted">{t(lang, "offline_desc")}</p>
          <div className="offline-tier-field">
            <span className="offline-tier-label" data-testid="offline-tier-selector-label">
              {t(lang, "select_ticket_tier")}
            </span>
            <div className="offline-tier-grid" data-testid="offline-tier-grid">
              {config.pools.map((pool) => (
                <button
                  type="button"
                  key={pool.tier}
                  className={`offline-tier-option pool-tier-${pool.tier}${offline.tier === pool.tier ? " active" : ""}`}
                  data-testid={`offline-tier-option-${pool.tier}`}
                  aria-pressed={offline.tier === pool.tier}
                  onClick={() => setOffline({ ...offline, tier: pool.tier })}
                >
                  <span>{t(lang, "tier_prefix")} {pool.tier}</span>
                  <strong>{money(pool.price)}</strong>
                  <small>{pool.name}</small>
                </button>
              ))}
            </div>
            <div className="offline-tier-selected" data-testid="offline-tier-selected">
              <Check size={14} /> {t(lang, "tier_prefix")} {offline.tier} · {money(config.pools.find((pool) => pool.tier === offline.tier)?.price || 0)} {t(lang, "per_ticket")}
            </div>
          </div>
          <label>{t(lang, "customer_name")}
            <input data-testid="offline-customer-name-input" value={offline.customer_name} onChange={(e) => setOffline({ ...offline, customer_name: e.target.value })} />
          </label>
          <label>{t(lang, "email_or_mobile")}
            <input data-testid="offline-identifier-input" value={offline.identifier} onChange={(e) => setOffline({ ...offline, identifier: e.target.value })} />
          </label>
          <label>{t(lang, "quantity")}
            <input data-testid="offline-quantity-input" type="number" min="1" value={offline.quantity} onChange={(e) => setOffline({ ...offline, quantity: Number(e.target.value) })} />
          </label>
          <button
            className="primary"
            data-testid="admin-issue-tickets-button"
            disabled={issuing || !offline.customer_name.trim() || !offline.identifier.trim() || offline.quantity < 1}
            onClick={issue}
          >
            {issuing ? t(lang, "allotting") : t(lang, "issue_direct")} <Ticket size={16} />
          </button>
        </section>
      </div>
      <DrawTool tier={tier} lang={lang} />
      <PaymentApprovals lang={lang} notify={setNotice} />
      <AdminSupportPanel notify={setNotice} />
      <AdminWithdrawals lang={lang} notify={setNotice} />
      {showUsers && <RegisteredUsersModal onClose={() => setShowUsers(false)} />}
      {notice && <div className="toast" data-testid="admin-notice">{notice}</div>}
    </div>
  );
}

// ---------- App ----------
function App() {
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState("home");
  const [config, setConfig] = useState({ pools: [], usdt_address: "", usdt_network: "BEP20" });
  const [checkout, setCheckout] = useState(null);
  const [prizePool, setPrizePool] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [configError, setConfigError] = useState("");
  const [lang, setLang] = useState(() => localStorage.getItem("sgln-lang") || "en");

  const refresh = () => api.get("/config")
    .then((response) => {
      setConfig(response.data);
      setConfigError("");
      return response.data;
    })
    .catch((error) => {
      setConfigError("Live ticket pools could not be loaded. Please retry.");
      throw error;
    });

  useEffect(() => {
    document.title = "LOTTO WORLD | Global Lottery Pools";
    refresh().catch(() => null);
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    localStorage.setItem("sgln-lang", lang);
    document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", config.theme || "midnight-gold");
  }, [config.theme]);

  if (user === undefined) return <div className="loading">{t(lang, "loading")}<span>•</span></div>;

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setView("home");
  };

  const requireAuth = (thenDo) => {
    if (!user) {
      setShowAuth(true);
      return null;
    }
    return thenDo();
  };

  const handleBuy = (pool) => requireAuth(() => setCheckout(pool));
  const handleProfile = () => requireAuth(() => setView("profile"));
  const handleAuthSuccess = (u) => {
    setUser(u);
    setShowAuth(false);
  };

  return (
    <>
      <Topbar
        user={user}
        lang={lang}
        setLang={setLang}
        onLogout={logout}
        onSignIn={() => setShowAuth(true)}
      />
      {user && view === "support" ? (
        <SupportCenter onBack={() => setView("profile")} />
      ) : user && view === "profile" ? (
        <Profile user={user} setView={setView} lang={lang} pools={config.pools} />
      ) : user && view === "admin" ? (
        <Admin config={config} refresh={refresh} lang={lang} />
      ) : (
        <>
          <div className="singapore-backdrop" data-testid="singapore-home-background" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/singapore-marina-bay.jpg`} data-testid="singapore-background-image" alt="" />
          </div>
          <div className="page home-page">
          <div className="page-heading">
            <div>
              <div className="eyebrow">{t(lang, "live_pools")}</div>
              <h1>{t(lang, "choose_line1")}<br /><em>{t(lang, "choose_line2")}</em></h1>
              {!user && <p className="muted hero-sub">{t(lang, "every_draw_desc")}</p>}
            </div>
            <div className="heading-actions">
              <span className="live-dot">{t(lang, "live")}</span>
              {user && (
                <button className="ghost" data-testid="profile-button" onClick={handleProfile}>
                  {t(lang, "my_tickets")} <Ticket size={16} />
                </button>
              )}
              {user && user.role === "admin" && (
                <button className="ghost" data-testid="admin-button" onClick={() => setView("admin")}>
                  {t(lang, "control_room")} <ShieldCheck size={16} />
                </button>
              )}
              {!user && (
                <button className="primary" data-testid="hero-signin-button" onClick={() => setShowAuth(true)}>
                  {t(lang, "sign_in")} <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
          {configError && (
            <div className="config-error" data-testid="pool-config-error" role="alert">
              <span>{configError}</span>
              <button className="ghost" data-testid="pool-config-retry-button" onClick={() => refresh().catch(() => null)}>Retry</button>
            </div>
          )}
          <div className="pool-stack">
            {config.pools.map((pool) => (
              <PoolCard key={pool.tier} pool={pool} onBuy={handleBuy} onViewPrizes={setPrizePool} lang={lang} />
            ))}
          </div>
          <HomeReferral user={user} lang={lang} onSignIn={() => setShowAuth(true)} />
          <div className="trust-strip trust-strip-bottom" data-testid="trust-strip">
            <div className="trust-item">
              <strong>$20M+</strong>
              <span>combined prize pools</span>
            </div>
            <div className="trust-item">
              <strong>4</strong>
              <span>independent tiers</span>
            </div>
            <div className="trust-item">
              <strong>USDT · BEP20</strong>
              <span>crypto payments</span>
            </div>
            <div className="trust-item">
              <strong>Provably fair</strong>
              <span>seeded random draws</span>
            </div>
          </div>
          <div className="home-note">
            <ShieldCheck size={17} /><span>{t(lang, "home_note")}</span>
          </div>
          </div>
        </>
      )}
      {showAuth && (
        <Auth onAuth={handleAuthSuccess} lang={lang} onClose={() => setShowAuth(false)} />
      )}
      {checkout && user && (
        <Checkout pool={checkout} config={config} onClose={() => setCheckout(null)} onComplete={refresh} lang={lang} />
      )}
      {prizePool && (
        <PrizeBreakdown pool={prizePool} onClose={() => setPrizePool(null)} lang={lang} />
      )}
    </>
  );
}

export default App;
